import { Button } from "@/components/ui/button-tw";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useInvoiceSelection from "@/hooks/invoice/use-invoice-selection";
import { ArrowDown, ArrowUp, ArrowUpDown, MoreHorizontal } from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/providers/auth";
import { downloadInvoice } from "@/pages/invoices/actions";

export const PAYABLE_STATUSES = new Set(["unpaid", "past_due", "unpaid_and_overdue"]);

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val ?? 0);

function SortHeader({ column, children }) {
  const sorted = column.getIsSorted();
  return (
    <button
      className="flex items-center gap-1.5 font-semibold text-left"
      onClick={() => column.toggleSorting(sorted === "asc")}
    >
      {children}
      {sorted === "asc" ? (
        <ArrowUp className="h-3.5 w-3.5" />
      ) : sorted === "desc" ? (
        <ArrowDown className="h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
      )}
    </button>
  );
}

function parseDate(val) {
  if (!val) return null;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
}

export const columns = [
  {
    accessorKey: "from",
    accessorFn: ({ invoice_info }) => invoice_info?.invoice_from?.name || "",
    header: ({ column }) => <SortHeader column={column}>From</SortHeader>,
    cell: ({ row }) => (
      <div className="font-medium text-gray-900">{row.getValue("from") || "—"}</div>
    ),
    enableSorting: true,
  },
  {
    accessorKey: "invoice_number",
    accessorFn: ({ invoice_info }) => invoice_info?.invoice_number || "",
    header: ({ column }) => <SortHeader column={column}>Invoice #</SortHeader>,
    cell: ({ row }) => (
      <div className="text-sm text-gray-500">{row.getValue("invoice_number") || "—"}</div>
    ),
    enableSorting: true,
  },
  {
    accessorKey: "sent",
    accessorFn: ({ invoice_info }) => parseDate(invoice_info?.invoice_send_date),
    header: ({ column }) => <SortHeader column={column}>Date Sent</SortHeader>,
    cell: ({ row }) => {
      const d = row.getValue("sent");
      return (
        <div className="text-sm text-gray-600">{d ? format(d, "MMM d, yyyy") : "—"}</div>
      );
    },
    enableSorting: true,
    sortingFn: "datetime",
  },
  {
    accessorKey: "due",
    accessorFn: ({ invoice_info }) => parseDate(invoice_info?.invoice_due_date),
    header: ({ column }) => <SortHeader column={column}>Due Date</SortHeader>,
    cell: ({ row }) => {
      const d = row.getValue("due");
      return (
        <div className="text-sm text-gray-600">{d ? format(d, "MMM d, yyyy") : "—"}</div>
      );
    },
    enableSorting: true,
    sortingFn: "datetime",
  },
  {
    accessorKey: "amount",
    accessorFn: ({ invoice_info }) => invoice_info?.invoice_total ?? 0,
    header: ({ column }) => <SortHeader column={column}>Amount</SortHeader>,
    cell: ({ row }) => (
      <div className="font-semibold text-gray-900">{formatCurrency(row.getValue("amount"))}</div>
    ),
    enableSorting: true,
    sortingFn: "basic",
  },
  {
    id: "actions",
    cell: ({ row }) => <ActionsCell row={row} />,
    enableSorting: false,
  },
];

// Kept for backward compatibility with mobile-list.jsx
export function mapStatusColor(status) {
  switch (status) {
    case "paid":
      return { main: "bg-green-400", accent: "bg-green-100", text: "text-green-400" };
    case "unpaid":
      return { main: "bg-yellow-400", accent: "bg-yellow-100", text: "text-yellow-400" };
    case "past_due":
    case "overdue":
    case "unpaid_and_overdue":
      return { main: "bg-red-400", accent: "bg-red-100", text: "text-red-400" };
    case "pending":
    case "processing":
      return { main: "bg-blue-400", accent: "bg-blue-100", text: "text-blue-400" };
    default:
      return { main: "bg-slate-400", accent: "bg-slate-100", text: "text-slate-400" };
  }
}

export function apiToTable({ invoice_info, invoice_id }) {
  return {
    id: invoice_id,
    from: invoice_info.invoice_from?.name,
    status: invoice_info.invoice_status.toLowerCase(),
    sent: invoice_info.invoice_send_date,
    amount: invoice_info.invoice_total,
  };
}

function ActionsCell({ row }) {
  const onSelect = useInvoiceSelection();
  const invoice = row.original;
  const { cognitoId } = useAuth();
  const status = invoice.invoice_info?.invoice_status?.toLowerCase() ?? "";
  const isPayable = PAYABLE_STATUSES.has(status);

  const handleDownload = async () => {
    const res = await downloadInvoice({
      userId: cognitoId,
      payload: { file_location: invoice.invoice_info?.invoice_full_location },
    });
    if (res?.downloadURLData) window.open(res.downloadURLData, "_blank");
  };

  return (
    <div className="flex justify-end items-center gap-2">
      <Button
        variant={isPayable ? "default" : "outline"}
        size="sm"
        className={
          isPayable
            ? "bg-skin-primary hover:opacity-90 text-skin-a11y h-8 px-4"
            : "h-8 px-4"
        }
        onClick={() => onSelect(invoice)}
      >
        {isPayable ? "Pay" : "View"}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => onSelect(invoice)}>
            View details
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() =>
              navigator.clipboard.writeText(invoice.invoice_info?.invoice_number ?? "")
            }
          >
            Copy invoice #
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDownload}>Download</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
